import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ColumnLayout,
  Container,
  Form,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
  Toggle,
} from '@cloudscape-design/components';
import { PageLayout, PageSkeleton, EmptyState, useAuth, useRoles, useNotify, createApiClient } from '@deloitte-us-consulting/gp-sdk';

/** Shapes served by /api/email/admin/* (api/src/routes/emailAdmin.ts). */
interface EmailConfig {
  paused: boolean;
  allowedRecipientDomains: string[];
  appDailyCap: number;
  userDailyCap: number;
  recipientDailyCap: number;
  globalDailyCap: number;
  updatedAt?: string;
  updatedBy?: string;
}
interface UsageRow {
  appId: string;
  displayName: string;
  emailEnabled: boolean;
  sentToday: number;
  cap: number;
}
interface Usage {
  globalToday: number;
  globalCap: number;
  apps: UsageRow[];
}
interface QueueDepths {
  queued: number;
  inFlight: number;
  deadLettered: number;
}
interface LogRow {
  emailId: string;
  callerKind: string;
  callerIdentity: string;
  to: string[];
  subject: string;
  status: 'queued' | 'sent' | 'failed';
  attempts: number;
  lastError?: string;
  createdAt: string;
}

export interface LimitsForm {
  domains: string;
  appDailyCap: string;
  userDailyCap: string;
  recipientDailyCap: string;
  globalDailyCap: string;
}

const API_URL = (import.meta.env.VITE_API_URL as string) ?? '';
const PLATFORM_APP_ID = 'gp-platform';
const csv = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
const CAP_KEYS = ['appDailyCap', 'userDailyCap', 'recipientDailyCap', 'globalDailyCap'] as const;
const STATUS_TYPE = { queued: 'pending', sent: 'success', failed: 'error' } as const;

export function toLimitsForm(cfg: EmailConfig): LimitsForm {
  return {
    domains: cfg.allowedRecipientDomains.join(', '),
    appDailyCap: String(cfg.appDailyCap),
    userDailyCap: String(cfg.userDailyCap),
    recipientDailyCap: String(cfg.recipientDailyCap),
    globalDailyCap: String(cfg.globalDailyCap),
  };
}

export function limitsError(f: LimitsForm): string | null {
  if (csv(f.domains).length === 0) return 'Add at least one recipient domain.';
  const badCap = CAP_KEYS.some((k) => {
    const n = Number(f[k]);
    return !Number.isInteger(n) || n < 1;
  });
  return badCap ? 'Every cap must be a whole number of at least 1.' : null;
}

export function toConfigBody(paused: boolean, f: LimitsForm) {
  return {
    paused,
    allowedRecipientDomains: csv(f.domains).map((d) => d.toLowerCase()),
    appDailyCap: Number(f.appDailyCap),
    userDailyCap: Number(f.userDailyCap),
    recipientDailyCap: Number(f.recipientDailyCap),
    globalDailyCap: Number(f.globalDailyCap),
  };
}

/**
 * Admin page for outbound email: pause switch, limits, today's usage, queue health,
 * recent sends, and an end-to-end test. Admin role (or gp-admin); the API enforces the same gate.
 */
export default function EmailAdmin() {
  const { accessToken } = useAuth();
  const { can } = useRoles();
  const isAllowed = can('Email:Manage', { appId: '*' });
  const { notify } = useNotify();
  const api = useMemo(() => createApiClient(API_URL, () => accessToken), [accessToken]);

  const [cfg, setCfg] = useState<EmailConfig | null>(null);
  const [limits, setLimits] = useState<LimitsForm | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [queue, setQueue] = useState<QueueDepths | null>(null);
  const [logApp, setLogApp] = useState(PLATFORM_APP_ID);
  const [log, setLog] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    const [c, u, q] = await Promise.all([
      api.get<EmailConfig>('/api/email/admin/config'),
      api.get<Usage>('/api/email/admin/usage'),
      api.get<QueueDepths | null>('/api/email/admin/queue'),
    ]);
    setCfg(c);
    setLimits(toLimitsForm(c));
    setUsage(u);
    setQueue(q);
  }, [api]);

  useEffect(() => {
    if (!isAllowed) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    load()
      .catch((e: unknown) => {
        if (!cancelled) notify({ type: 'error', header: 'Could not load email settings', content: (e as Error).message });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Intentionally NOT depending on `notify` (fresh each render) — see DataSourcesAdmin.
  }, [load, isAllowed]);

  useEffect(() => {
    if (!isAllowed) return;
    let cancelled = false;
    api
      .get<LogRow[]>(`/api/email/admin/log/${encodeURIComponent(logApp)}`)
      .then((rows) => {
        if (!cancelled) setLog(rows);
      })
      .catch(() => {
        if (!cancelled) setLog([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api, isAllowed, logApp]);

  if (!isAllowed) {
    return (
      <PageLayout title="Email">
        <EmptyState title="Platform admin access required" description="Ask an admin to grant you the Admin role." />
      </PageLayout>
    );
  }
  if (loading) {
    return (
      <PageLayout title="Email">
        <PageSkeleton label="Loading email settings…" />
      </PageLayout>
    );
  }
  if (!cfg || !limits || !usage) {
    return (
      <PageLayout title="Email">
        <EmptyState title="Couldn't load email settings" description="Check that the platform API is deployed and you hold the Admin role, then reload." />
      </PageLayout>
    );
  }

  const error = limitsError(limits);
  const putConfig = async (body: ReturnType<typeof toConfigBody>, resetForm: boolean) => {
    setSaving(true);
    try {
      const next = await api.put<EmailConfig>('/api/email/admin/config', body);
      setCfg(next);
      if (resetForm) setLimits(toLimitsForm(next));
      notify({ type: 'success', header: 'Email settings saved', content: next.paused ? 'New email is paused.' : 'Settings updated.' });
    } catch (e) {
      notify({ type: 'error', header: 'Save failed', content: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };
  const saveLimits = () => {
    if (error) return;
    void putConfig(toConfigBody(cfg.paused, limits), true);
  };
  // The pause switch is the emergency stop, so it must always work: it sends the
  // STORED limits with the new flag, never the form's (possibly invalid or unsaved)
  // edits, and leaves those edits in the form untouched.
  const setPaused = (paused: boolean) => void putConfig(toConfigBody(paused, toLimitsForm(cfg)), false);
  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post<{ data: { emailId: string } }>('/api/email/admin/test');
      notify({ type: 'success', header: 'Test email queued', content: `Check your inbox in a minute or two (id ${r.data.emailId}).` });
      await load();
    } catch (e) {
      notify({ type: 'error', header: 'Test email failed', content: (e as Error).message });
    } finally {
      setTesting(false);
    }
  };
  const setLimit = (key: keyof LimitsForm) => (value: string) => setLimits((l) => (l ? { ...l, [key]: value } : l));
  const appOptions = usage.apps.map((a) => ({ value: a.appId, label: a.displayName }));

  return (
    <PageLayout title="Email">
      <SpaceBetween size="l">
        <Container
          header={
            <Header actions={<Button loading={testing} onClick={sendTest}>Send test email to me</Button>}>
              Status
            </Header>
          }
        >
          <ColumnLayout columns={3} variant="text-grid">
            <Toggle checked={cfg.paused} disabled={saving} onChange={({ detail }) => setPaused(detail.checked)}>
              Pause new email
            </Toggle>
            <div>
              <Box variant="awsui-key-label">Sent today (all apps)</Box>
              <div>{`${usage.globalToday} / ${usage.globalCap}`}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Queue</Box>
              {queue ? (
                <SpaceBetween size="xxs">
                  <div>{`${queue.queued} queued, ${queue.inFlight} sending`}</div>
                  <StatusIndicator type={queue.deadLettered > 0 ? 'error' : 'success'}>{`${queue.deadLettered} dead-lettered`}</StatusIndicator>
                </SpaceBetween>
              ) : (
                <div>Not configured</div>
              )}
            </div>
          </ColumnLayout>
        </Container>

        <Container header={<Header description="Changes apply to the next email; no redeploy.">Limits and recipients</Header>}>
          <Form
            errorText={error ?? undefined}
            actions={
              <Button variant="primary" loading={saving} disabled={!!error} onClick={saveLimits}>
                Save
              </Button>
            }
          >
            <SpaceBetween size="m">
              <FormField label="Allowed recipient domains" description="Comma-separated, matched exactly (no subdomains).">
                <Input value={limits.domains} onChange={({ detail }) => setLimit('domains')(detail.value)} />
              </FormField>
              <ColumnLayout columns={4}>
                <FormField label="Per-app daily cap">
                  <Input value={limits.appDailyCap} inputMode="numeric" onChange={({ detail }) => setLimit('appDailyCap')(detail.value)} />
                </FormField>
                <FormField label="Per-user daily cap">
                  <Input value={limits.userDailyCap} inputMode="numeric" onChange={({ detail }) => setLimit('userDailyCap')(detail.value)} />
                </FormField>
                <FormField label="Per-recipient daily cap">
                  <Input value={limits.recipientDailyCap} inputMode="numeric" onChange={({ detail }) => setLimit('recipientDailyCap')(detail.value)} />
                </FormField>
                <FormField label="Global daily cap">
                  <Input value={limits.globalDailyCap} inputMode="numeric" onChange={({ detail }) => setLimit('globalDailyCap')(detail.value)} />
                </FormField>
              </ColumnLayout>
            </SpaceBetween>
          </Form>
        </Container>

        <Table
          header={<Header>Today by app</Header>}
          items={usage.apps}
          columnDefinitions={[
            { header: 'App', cell: (a) => a.displayName },
            { header: 'Email enabled', cell: (a) => (a.emailEnabled ? 'Yes' : 'No') },
            { header: 'Recipients today', cell: (a) => a.sentToday },
            { header: 'Cap', cell: (a) => a.cap },
          ]}
        />

        <Table
          header={
            <Header
              actions={
                <Select
                  selectedOption={appOptions.find((o) => o.value === logApp) ?? null}
                  options={appOptions}
                  onChange={({ detail }) => setLogApp(detail.selectedOption.value ?? PLATFORM_APP_ID)}
                />
              }
            >
              Recent sends
            </Header>
          }
          items={log}
          empty={<Box textAlign="center">No email sent by this app in the last 90 days.</Box>}
          columnDefinitions={[
            { header: 'Queued', cell: (r) => new Date(r.createdAt).toLocaleString() },
            { header: 'Subject', cell: (r) => r.subject },
            { header: 'To', cell: (r) => r.to.join(', ') },
            { header: 'Status', cell: (r) => <StatusIndicator type={STATUS_TYPE[r.status]}>{r.status}</StatusIndicator> },
            { header: 'Attempts', cell: (r) => r.attempts },
            { header: 'Error', cell: (r) => r.lastError ?? '—' },
          ]}
        />
      </SpaceBetween>
    </PageLayout>
  );
}
